<script>
import RawData from "@/components/widgets/raw-data.vue";
import LoadingTemplate from "@/components/widgets/loading-template.vue";
import XLSX from "xlsx";
import { saveAs } from "file-saver";

export default {
    name: "JevEntries",
    components: {
        RawData,
        LoadingTemplate
    },
    props: {
        entries: {
            type: Array,
            default: () => [],
        },
        journals: {
            type: Array,
            default: () => [],
        },
        statusType: {
            type: String,
            default: "draft", // draft or posted
        },
        type: {
            type: String,
            default: "jev",
        },
        from: {
            type: String,
            default: "jev",
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            filter: "",
            currentPage: 1,
            perPage: 10,
            pageOptions: [10, 25, 50, 100],
            isMultipleSelection: false
        };
    },
    computed: {
        filtered() {
            let data = this.entries;
            if (this.filter && this.filter.trim() != "") {
                data = data.filter((item) => {
                    return item.filterKey
                        .toLowerCase()
                        .includes(this.filter.toLowerCase());
                });
            }
            var total = this.currentPage * this.perPage;
            var currentData = data.slice(total - this.perPage, total);
            if (this.currentPage > 1 && currentData.length == 0) {
                total = (this.currentPage - 1) * this.perPage;
                currentData = data.slice(total - this.perPage, total);
            }
            return currentData;
        },
        rows() {
            let data = this.entries;
            if (this.filter && this.filter.trim() != "") {
                data = data.filter((item) => {
                    return item.filterKey
                        .toLowerCase()
                        .includes(this.filter.toLowerCase());
                });
            }
            return data.length;
        },
        selectedEntries() {
            return this.entries.filter(item => item.selected);
        },
        isSelectedAll: {
            get() {
                return this.entries.length > 0 && this.entries.every(item => item.selected);
            },
            set(value) {
                this.entries.forEach(item => {
                    item.selected = value;
                });
            }
        },
    },
    methods: {
        addJEVEntry() {
            this.$emit("add-entry");
        },
        getJevDetails(item) {
            item.opened = !item.opened;
        },
        formatText(text) {
            return text.replace(/(?:\r\n|\r|\n)/g, "<br>");
        },
        editJevEntry(item) {
            this.$emit("edit-entry", item);
        },
        deleteJevEntry(item) {
            this.$emit("delete-entry", item);
        },
        printJev(item) {
            this.$emit("print", item);
        },
        getTotalDebit(items) {
            return items.reduce((total, item) => {
                return total + parseFloat(this.getExactAmt(item.debit) || 0);
            }, 0);
        },
        getTotalCredit(items) {
            return items.reduce((total, item) => {
                return total + parseFloat(this.getExactAmt(item.credit) || 0);
            }, 0);
        },
        postJevEntry(item) {
            this.$emit("post-entry", item);
        },
        approveJevEntry(item) {
            this.$emit("approveJevEntry", item);
        },
        getSL(detail) {
            if (detail.subsidiaryLedgerObj) return;
            this.$set(detail, 'subsidiaryLedgerObj', null);
            this.$store.dispatch('subsidiaryledger/getSingleSubsidiaryLedger', detail.subsidiaryLedgerId)
                .then(res => {
                    if (res.data.error) {
                        return this.showToast(res.data.errorMessage, 'error');
                    }
                    this.$set(detail, 'subsidiaryLedgerObj', res.data);
                })
                .catch(err => {
                    this.showToast('Something went wrong while fetching Subsidiary Ledger!', 'error');
                });
        },
        async postNow(form, jev){
            await this.$store.dispatch('jev/postJev', {
                id: jev.id,
                jevNumber: form.jevNumber,
            })
            .then(res => {
                if (res.data.error) {
                    return this.showToast(res.data.errorMessage, 'error');
                }
                jev.isPosted = true;
                jev.jevNumber = form.jevNumber;
                this.showToast('Journal Entry posted successfully!', 'success');
            })
            .catch(err => {
                this.showToast('Something went wrong while posting Journal Entry!', 'error');
            });
        },
        getJournal(id) {
            let journal = this.journals.find(j => j.id === id);
            return journal ? journal.journalTypeName : 'N/A';
        },
        downloadTemplate(){
            // Create instructions worksheet
            const instructions = [
                ['JOURNAL ENTRY VOUCHER TEMPLATE'],
                [''],
                ['INSTRUCTIONS:'],
                ['1. Fill in the following columns for each journal entry:'],
                ['   - Type: Enter one of the following codes [ADAJ, CkDJ, CDJ, CRJ, GJ]'],
                ['   - Date: Enter date in MM/DD/YYYY format'],
                ['   - JEV No: Journal Entry Voucher Number'],
                ['   - Description: Transaction description'],
                ['   - Source Reference: Source document reference'],
                ['   - UACS: Uniform Account Code Structure'],
                ['   - Account: Account name/description'],
                ['   - Debit: Debit amount (numbers only, no commas)'],
                ['   - Credit: Credit amount (numbers only, no commas)'],
                [''],
                ['2. Type Codes:'],
                ['   - ADAJ: Adjusting Journal'],
                ['   - CkDJ: Check Disbursement Journal'],
                ['   - CDJ: Cash Disbursement Journal'],
                ['   - CRJ: Cash Receipts Journal'],
                ['   - GJ: General Journal'],
                [''],
                ['3. Important Notes:'],
                ['   - Do not modify the column headers in the Template sheet'],
                ['   - Ensure debits equal credits for each journal entry'],
                ['   - All required fields must be filled'],
                ['   - Date format must be consistent'],
            ];

            // Create template worksheet with headers
            const template = [
                ['Type', 'Date', 'JEV No', 'Description', 'Source Reference', 'UACS', 'Account', 'Debit', 'Credit'],
            ];

            // Create workbook
            const wb = XLSX.utils.book_new();
            const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
            const wsTemplate = XLSX.utils.aoa_to_sheet(template);
            
            // Set column widths
            wsTemplate['!cols'] = [
                { wch: 10 },  // Type
                { wch: 12 },  // Date
                { wch: 15 },  // JEV No
                { wch: 30 },  // Description
                { wch: 20 },  // Source Reference
                { wch: 12 },  // UACS
                { wch: 20 },  // Account
                { wch: 12 },  // Debit
                { wch: 12 },  // Credit
            ];
            
            XLSX.utils.book_append_sheet(wb, wsTemplate, 'Template');
            XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
            
            // Generate Excel file
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/octet-stream' });
            saveAs(blob, 'JEV_Template.xlsx');
            
            this.showToast('Template downloaded successfully!', 'success');
        },
        uploadExcel(){
            this.$emit('upload-excel');
        },
    }
}
</script>
<template>
    <div class="jev-entries">
        <div class="card">
            <div class="card-body bg-soft" :class="{'bg-info': statusType === 'draft', 'bg-success': statusType === 'posted'}">
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        mb-3
                    "
                >
                    <div>
                        <h4 class="card-title mb-0">
                            {{statusType.charAt(0).toUpperCase() + statusType.slice(1)}} Journal Entries
                        </h4>
                        <p class="card-title-desc mb-0">
                            List of {{statusType}} Journal Entries <span v-if="statusType === 'draft'">(<a href="javascript:void(0);" @click="isMultipleSelection = !isMultipleSelection">{{isMultipleSelection ? 'Multiple' : 'Single'}} Selection</a>)</span>
                        </p>
                    </div>
                    <div class="d-flex">
                        <button 
                            v-if="isMultipleSelection && selectedEntries.length" 
                            type="button" 
                            class="btn btn-danger text-nowrap me-1" 
                            @click="$emit('bulk-remove', selectedEntries)"
                        >
                            <i class="bx bx-trash"></i> Remove Selected ({{selectedEntries.length}})
                        </button>
                        <button 
                            v-if="isMultipleSelection && selectedEntries.length" 
                            type="button" 
                            class="btn btn-warning text-nowrap me-1" 
                            @click="$emit('bulk-post', selectedEntries)"
                        >
                            <i class="bx bx-receipt"></i> Post Selected ({{selectedEntries.length}})
                        </button>
                        <b-dropdown 
                            id="dropdown-dropleft"
                            variant="success btn me-1" 
                            toggle-class="text-decoration-none"
                            menu-class="dropdown-menu-end" 
                            v-if="statusType === 'draft'"
                            no-caret
                        >
                            <template #button-content>
                                <i class="bx bx-file"></i> Excel Options
                            </template>
                            <b-dropdown-item
                                @click="downloadTemplate"
                            >
                                Download Template
                            </b-dropdown-item>
                                <b-dropdown-item
                                @click="uploadExcel"
                            >
                                Upload Excel
                            </b-dropdown-item>
                        </b-dropdown>
                        <button type="button" class="btn btn-primary me-1 text-nowrap" @click="addJEVEntry" v-if="statusType === 'draft'">
                            <i class="bx bx-plus"></i> Add Journal Entry
                        </button>
                        <template v-if="entries.length > 0">
                            <input
                                autocomplete="off"
                                type="text"
                                v-model="filter"
                                placeholder="Search Journal Entries..."
                                class="form-control"
                            />
                            <b-form-select
                                class="form-select ms-1 w-25"
                                v-model="perPage"
                                :options="pageOptions"
                            ></b-form-select>
                        </template>
                    </div>
                </div>
                <div v-if="loading" class="text-center my-5 py-5">
                    <LoadingTemplate class="my-5"/>
                </div>
                <div v-if="!loading && entries.length == 0" class="text-center">
                    <h5>No data available</h5>
                </div>
                <div v-if="!loading && entries.length && !filtered.length" class="text-center">
                    <h5>Search not found</h5>
                </div>
                <template v-if="entries.length">
                    <div
                        class="
                            form-check
                            form-checkbox-outline
                            form-check-success
                            mb-0
                        "
                        v-if="isMultipleSelection"
                    >
                        <input
                            type="checkbox"
                            id="select-all-entries"
                            class="form-check-input"
                            v-model="isSelectedAll"
                        />
                        <label for="select-all-entries">Select All</label>
                    </div>
                    <div
                        role="tablist"
                        v-for="(x, index) in filtered"
                        :key="x.id"
                    >
                    
                        <div class="d-flex align-items-center gap-1">
                            <div
                                class="
                                    form-check
                                    form-checkbox-outline
                                    form-check-success
                                    mb-0
                                "
                                v-if="isMultipleSelection"
                            >
                                <input
                                    type="checkbox"
                                    :id="`entry-${x.id}`"
                                    class="form-check-input"
                                    v-model="x.selected"
                                />
                            </div>
                            <b-card
                                no-body
                                class="mb-1 shadow-none w-100"
                                :style="{'z-index': `${filtered.length - index}`}"
                            >
                                <RawData :data="x" v-if="$store.state.decrypt.isAdmin()" placement="left"/>
                                    
                                    <b-card-header
                                        header-tag="header"
                                        role="tab"
                                        header-class="cursor-pointer"
                                        @click="getJevDetails(x)"
                                    >
                                        <div
                                            class="
                                                d-flex
                                                justify-content-between
                                                align-items-center
                                            "
                                        >
                                            <h6 class="m-0">
                                                <a class="text-dark" href="javascript: void(0);">
                                                    &nbsp;
                                                    <span class="text-warning">[{{ x.jevNumber}}]</span>
                                                    :&nbsp;
                                                    {{ x.description }}
                                                </a>
                                            </h6>
                                            <div class="d-flex align-items-center">
                                                <div>
                                                    <span class="d-flex align-items-center text-truncate">
                                                        <small v-if="!x.opened">Click here to expand</small>
                                                        <i 
                                                            class="bx bx-chevron-right rotate font-size-18 ms-3"
                                                            :class="{'rotate-90': x.opened }"
                                                        ></i
                                                    ></span>
                                                </div>
                                            </div>
                                        </div>
                                    </b-card-header>
                                    <b-collapse
                                        :visible="x.opened"
                                        :accordion="`jev-accordion-${statusType}-${x.id}`"
                                        role="tabpanel"
                                    >
                                        <b-card-body>
                                            <div class="d-flex align-items-center justify-content-between cursor-pointer border-bottom pb-2 mb-2" >
                                                <h5 class="mb-0"><i class="bx bx-receipt"></i> Journal Entry Details</h5>
                                            </div>
                                            <div class="row">
                                                <div class="col-md-6 border-end">
                                                    
                                                    <p class="mb-0 font-size-14">Journal Type: <span class="text-white font-weight-bold">{{x.journalTypeId ? getJournal(x.journalTypeId) : '-'}}</span></p>
                                                    <p class="mb-0 font-size-14 d-flex gap-1">Description: <span class="text-white font-weight-bold">{{x.description || '-'}}</span></p>
                                                    <p class="mb-0 font-size-14">Responsibility Center: <span class="text-white font-weight-bold">{{x.responsibilityCenter || '-'}}</span></p>
                                                    <p class="mb-0 font-size-14">Journal Entry Date: <span class="text-white font-weight-bold">{{x.jevDate ? formatDateShort(new Date(x.jevDate)) : '-'}}</span></p>
                                                    <hr class="my-1">
                                                    <div class="d-flex gap-1" v-if="x.sourceDocument">
                                                        <span>Source Documents:</span> <span class="text-white font-weight-bold" v-html="formatText(x.sourceDocument)"></span>
                                                    </div>
                                                    <div class="d-flex gap-1">
                                                        <span>Fund Source:</span> <span class="text-white font-weight-bold">{{x.fundSource}}</span>
                                                    </div>
                                                    <div class="d-flex gap-1">
                                                        <span>Created By:</span> <span class="text-white font-weight-bold">{{x.createdByUser}}</span>
                                                    </div>
                                                </div>
                                                <div class="col-md-6">
                                                    <div class="d-flex align-items-end justify-content-between mb-1">
                                                        <label for="jevDetails" class="mb-0">Journal Entry Details:</label>
                                                        <div class="text-right font-size-16 ">
                                                            <div>Total Debit: <span class="text-success font-weight-bold">{{formatCurrency(getTotalDebit(x.jevDetails || []))}}</span></div>
                                                            <div>Total Credit: <span class="text-warning font-weight-bold">{{formatCurrency(getTotalCredit(x.jevDetails || []))}}</span></div>
                                                        </div>
                                                    </div>
                                                    <div class="py-2 rounded mb-2">
                                                        <table class="table table-sm align-middle table-bordered border-primary mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th class="py-2">Account</th>
                                                                    <th class="py-2 text-center" style="width:120px;">Debit</th>
                                                                    <th class="py-2 text-center" style="width:120px;">Credit</th>
                                                                    <th class="py-2 text-center" style="width:100px;">SL</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <template v-if="!x.jevDetails || x.jevDetails.length == 0">
                                                                    <tr>
                                                                        <td colspan="4" class="text-center">No journal details available</td>
                                                                    </tr>
                                                                </template>
                                                                <tr v-for="(detail, detailIndex) in x.jevDetails" :key="detailIndex">
                                                                    <td class="py-2">
                                                                        <div class="position-relative">
                                                                            <RawData :data="detail" v-if="$store.state.decrypt.isAdmin()" />
                                                                            {{detail.account}}
                                                                        </div>
                                                                    </td>
                                                                    <td class="py-2 text-center">{{detail.debit ? formatCurrency(detail.debit) : '-'}}</td>
                                                                    <td class="py-2 text-center">{{detail.credit ? formatCurrency(detail.credit) : '-'}}</td>
                                                                    <!-- <td class="py-2 text-center">
                                                                        <span v-if="detail.isSundry" class="badge bg-info">Yes</span>
                                                                        <span v-else class="badge bg-secondary">No</span>
                                                                    </td> -->
                                                                    <td  class="py-2 text-center">
                                                                        <template v-if="!detail.subsidiaryLedgerId">
                                                                            -
                                                                        </template>
                                                                        <template v-else>
                                                                            <a href="javascript:void(0);" class="font-size-14 mb-0" :id="`A-${detailIndex}`" @click="getSL(detail)">{{ detail.subsidiaryLedgerId ? detail.subsidiaryLedger : '-' }}</a>
                                                                            <b-popover :target="`A-${detailIndex}`" placement="left" triggers="click blur">
                                                                                <template #title>Subsidiary Ledger</template>
                                                                                <div>
                                                                                    <div v-if="!detail.subsidiaryLedgerObj" class="text-center">
                                                                                        <b-spinner small></b-spinner>
                                                                                        <small class="d-block mt-1">Loading...</small>
                                                                                    </div>
                                                                                    <div v-else>
                                                                                        <div class="text-truncate">{{ detail.subsidiaryLedgerObj.code }}</div>
                                                                                        <div class="font-size-13 text-success font-weight-bold">{{ detail.subsidiaryLedgerObj.clientDto.name }}</div>
                                                                                        <div>{{ detail.subsidiaryLedgerObj.subsidiaryLedgerType }}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </b-popover>
                                                                        </template>
                                                                    </td>
                                                                </tr>
                                                                <tr class="table-secondary font-weight-bold">
                                                                    <td class="py-2">Total</td>
                                                                    <td class="py-2 text-center">{{ formatCurrency(getTotalDebit(x.jevDetails || [])) }}</td>
                                                                    <td class="py-2 text-center">{{ formatCurrency(getTotalCredit(x.jevDetails || [])) }}</td>
                                                                    <td class="py-2"></td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="d-flex justify-content-end gap-1 border-top pt-3 mt-2">
                                                <template  v-if="statusType === 'draft'">
                                                    <button type="button" class="btn btn-outline-info btn-rounded" @click="editJevEntry(x)">
                                                        <i class="bx bx-edit"></i> Edit Journal Entry
                                                    </button>
                                                    <button type="button" class="btn btn-outline-danger btn-rounded" @click="deleteJevEntry(x)">
                                                        <i class="bx bx-trash"></i> Delete Journal Entry
                                                    </button>
                                                </template>
                                                <button 
                                                    type="button" 
                                                    class="btn btn-outline-warning btn-rounded" 
                                                    @click="$emit('attachment', {att: ['jev'], id: x.id})"
                                                >
                                                    <i class="bx bx-paperclip rotate rotate-45"></i> Attachments
                                                </button>
                                                <button type="button" class="btn btn-outline-purple btn-rounded" @click="printJev(x)">
                                                    <i class="bx bx-printer"></i> Print JEV
                                                </button>
                                                <button 
                                                    type="button" 
                                                    class="btn btn-outline-success btn-rounded" 
                                                    @click="postJevEntry(x)"
                                                    v-if="statusType === 'draft'"
                                                >
                                                    <i class="bx bx-check"></i> Post Journal Entry
                                                </button>
                                                <!-- <button 
                                                    type="button" 
                                                    class="btn btn-outline-primary btn-rounded" 
                                                    @click="approveJevEntry(x)"
                                                    v-if="type === 'posted'"
                                                >
                                                    <i class="bx bx-check-double"></i> Approve Journal Entry
                                                </button> -->
                                            </div>
                                        </b-card-body>
                                    </b-collapse>
                            </b-card>
                        </div>
                    </div>
                </template>
                <div class="d-flex justify-content-end mt-2" v-if="entries.length">
                    <div class="dataTables_paginate paging_simple_numbers">
                        <ul class="pagination pagination-rounded mb-0">
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="rows"
                                :per-page="perPage"
                            ></b-pagination>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.hoverable-link:hover {
    text-decoration: underline;
}

.hover-header:hover {
    background-color: rgba(255, 255, 255, 0.05);
}

.rotate {
    transition: transform 0.3s ease;
}

.rotate-90 {
    transform: rotate(90deg);
}

.rotate-45 {
    transform: rotate(45deg);
}
</style>